/**
 * Authentication context for CiteOps.ai Dashboard
 *
 * Wraps Supabase Auth and exposes:
 * - user / session / loading state
 * - signInWithGoogle, signInWithMagicLink, signOut methods
 *
 * Subscribes to onAuthStateChange on mount and cleans up on unmount.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import type { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { gtagEvent } from '../lib/gtag'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface AuthContextValue {
  /** The currently authenticated Supabase user, or null. */
  user: User | null
  /** The current Supabase session, or null. */
  session: Session | null
  /** True while the initial session is being resolved. */
  loading: boolean
  /** Initiate Google OAuth sign-in (redirects to Google). */
  signInWithGoogle: () => Promise<{ error: AuthError | null }>
  /** Send a passwordless magic-link email. */
  signInWithMagicLink: (email: string) => Promise<{ error: AuthError | null }>
  /** Sign the current user out. */
  signOut: () => Promise<{ error: AuthError | null }>
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  /* ---------- bootstrap + subscription ---------- */

  useEffect(() => {
    // 1. Resolve existing session (e.g. refresh token in storage)
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession)
      setUser(currentSession?.user ?? null)
      setLoading(false)
    })

    // 2. Listen for future auth state changes (sign-in, sign-out, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession)
      setUser(newSession?.user ?? null)
      setLoading(false)
      if (event === 'SIGNED_IN') {
        gtagEvent('login', { method: newSession?.user?.app_metadata?.provider ?? 'unknown' })
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  /* ---------- auth methods ---------- */

  const signInWithGoogle = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    return { error }
  }, [])

  const signInWithMagicLink = useCallback(async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    return { error }
  }, [])

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  }, [])

  /* ---------- memoised value ---------- */

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      loading,
      signInWithGoogle,
      signInWithMagicLink,
      signOut,
    }),
    [user, session, loading, signInWithGoogle, signInWithMagicLink, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

/**
 * Consume the AuthContext.
 * Must be rendered inside an <AuthProvider>.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
